import { format } from "date-fns";
import { EOSNode } from "../../../build/types";
import "./style.scss";
import { Fragment } from "react";
import { EOSData } from "../../../build/printLayoutTypes";

interface EOSContainerProps {
    node: EOSNode;
    eosData: EOSData;
    receiptLabels?: string;
    formatCurrency: (amount: number) => string;
    additionalProps?: {
        cashEnabled?: boolean;
    };
}

const EOSLayout = ({ node, eosData, formatCurrency, additionalProps }: EOSContainerProps) => {
    return (
        <div className="eos-layout eos">
            <div>
                <div className="flex-row-space m-y-big">
                    <p className="bold">END OF SESSION REPORT</p>
                    <p className="bold">#{eosData.count}</p>
                </div>
                <hr className="divider" />

                <div className="eos-header">
                    <p>Report from: {format(eosData.dateReportFrom, "dd MMMM yyyy, hh:mm aaaa")}</p>
                    <p>Opened by: {eosData.openedBy}</p>
                    <p>Report until: {format(eosData.dateReportUntil, "dd MMMM yyyy, hh:mm aaaa")}</p>
                    <p>Closed by: {eosData.closedBy}</p>
                </div>
                <hr className="divider" />
            </div>

            {node.settings.showSessionSummary ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">SESSION SUMMARY</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        {eosData.sessionSummaryBlock.map(item => (
                            <div className="flex-row-space m-y-big">
                                <p className={item.bold ? "bold" : ""}>{item.label}</p>
                                <p className={item.bold ? "bold" : ""}>{formatCurrency(item.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {node.settings.showPaymentBreakdown ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">PAYMENTS BREAKDOWN</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        {eosData.paymentsBreakDownBlock.map(item => (
                            <div className="flex-row-space m-y-big">
                                <p className={item.bold ? "bold" : ""}>{item.label}</p>
                                <p className={item.bold ? "bold" : ""}>{formatCurrency(item.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {additionalProps?.cashEnabled && node.settings.showCashDrawer ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">CASH DRAWER</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        {eosData.cashDrawerBlock.map(item => (
                            <div className="flex-row-space m-y-big">
                                <p className={item.bold ? "bold" : ""}>{item.label}</p>
                                <p className={item.bold ? "bold" : ""}>{formatCurrency(item.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {node.settings.showFloatAdjustment ? (
                <div>
                    <p className="bold">FLOAT ADJUSTMENTS</p>
                    <hr className="divider" />
                    <div className="gap-layout">
                        <div className="eos-3-grid">
                            <p>Name</p>
                            <p>Time</p>
                            <p>Amount</p>
                        </div>

                        {eosData.transactionBlock.transactions.map((transaction, index) => {
                            return (
                                <Fragment key={`transaction-${index}`}>
                                    <div className="gap-layout-small">
                                        <div className="eos-3-grid">
                                            <p>{transaction.user}</p>
                                            <p>{format(transaction.time, "hh:mm")}</p>
                                            <p>
                                                {transaction.type === "add" ? "+" : "-"}
                                                {formatCurrency(transaction.amount)}
                                            </p>
                                        </div>
                                        {transaction.note ? <p>Note: {transaction.note}</p> : null}
                                    </div>
                                </Fragment>
                            );
                        })}
                    </div>
                    <hr className="divider" />
                    <div className="eos-2-grid">
                        <p>Total</p>
                        <p>{formatCurrency(eosData.transactionBlock.total)}</p>
                    </div>
                </div>
            ) : null}

            {node.settings.showTaxBreakdown ? (
                <div>
                    <p className="bold">TAX BREAKDOWN</p>
                    <hr className="divider" />

                    <div>
                        <div className="gap-layout">
                            {eosData.taxBreakdown.taxes.map((tax, index) => (
                                <div className="gap-layout-small" key={`tax-${index}`}>
                                    <p className="bold">
                                        {tax.name} {`(${tax.rate})`}
                                    </p>
                                    <div className="eos-3-grid">
                                        <p>Gross</p>
                                        <p>Tax</p>
                                        <p>Net</p>
                                    </div>
                                    <div className="eos-3-grid">
                                        <p>{formatCurrency(tax.gross)}</p>
                                        <p>{formatCurrency(tax.gross - tax.net)}</p>
                                        <p>{formatCurrency(tax.net)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <hr className="divider" />

                        <div className="gap-layout-small">
                            <p className="bold">Total</p>
                            <div className="eos-3-grid">
                                <p>Gross</p>
                                <p>Tax</p>
                                <p>Net</p>
                            </div>
                            <div className="eos-3-grid">
                                <p>{formatCurrency(eosData.taxBreakdown.total.gross)}</p>
                                <p>{formatCurrency(eosData.taxBreakdown.total.gross - eosData.taxBreakdown.total.net)}</p>
                                <p>{formatCurrency(eosData.taxBreakdown.total.net)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {node.settings.showRefunds ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">REFUNDS BY TENDER TYPE</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        <div className="flex-row-space m-y-big">
                            <p>Tender type</p>
                            <p>Amount</p>
                        </div>
                        {eosData.refundBlock.tender.map(item => (
                            <div className="flex-row-space m-y-big">
                                <p>{item.label}</p>
                                <p>{formatCurrency(item.amount)}</p>
                            </div>
                        ))}
                        <div className="flex-row-space m-y-big">
                            <p>Total refunds</p>
                            <p>{formatCurrency(eosData.refundBlock.total)}</p>
                        </div>
                    </div>
                </div>
            ) : null}

            {node.settings.showStaffBreakdown ? (
                <div>
                    <p className="bold">STAFF SALES</p>
                    <hr className="divider" />

                    <div>
                        <div className="gap-layout">
                            <div className="eos-3-grid">
                                <p className="bold">Staff Name</p>
                                <p className="bold">Order Qty</p>
                                <p className="bold">Gross</p>
                            </div>
                            {eosData.staffBlock.transactions.map((item, index) => (
                                <div className="gap-layout-small" key={`tax-${index}`}>
                                    <div className="eos-3-grid">
                                        <p>{item.name}</p>
                                        <p>{item.quantity}</p>
                                        <p>{formatCurrency(item.gross)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <hr className="divider" />

                        <div className="gap-layout-small">
                            <div className="eos-3-grid">
                                <p>Total</p>
                                <p>{eosData.staffBlock.total.quantity}</p>
                                <p>{formatCurrency(eosData.staffBlock.total.gross)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {node.settings.showDiscount ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">DISCOUNTS</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        <div className="flex-row-space m-y-big">
                            <p>Discount type</p>
                            <p>Amount</p>
                        </div>
                        {eosData.discountBlock.discounts.map(item => (
                            <div className="flex-row-space m-y-big">
                                <p>{item.type}</p>
                                <p>{formatCurrency(item.amount)}</p>
                            </div>
                        ))}
                        <div className="flex-row-space m-y-big">
                            <p>Total discounts</p>
                            <p>{formatCurrency(eosData.discountBlock.total)}</p>
                        </div>
                    </div>
                </div>
            ) : null}

            {node.settings.showClosingNotes ? (
                <div>
                    <div className="flex-row-space m-y-big">
                        <p className="bold">CLOSING NOTES</p>
                    </div>
                    <hr className="divider" />
                    <div>
                        <p>{eosData.closingNotes}</p>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default EOSLayout;
